// ════════════════════════════════════════════════════════════════════════
//  Schulen, Stammdaten, Lizenzhistorie
//
//  Dieser Bereich hängt NICHT am DraftContext. Die anderen drei arbeiten an
//  derselben Lehrgangsfassung und teilen deshalb einen Zustand; mit Schulen hat
//  die Fassung nichts zu tun. Steht hier, weil die Versuchung groß ist, den
//  vorhandenen Kontext einfach mitzubenutzen.
//
//  Gelesen wird in vier getrennten Abfragen statt über eingebettete Ressourcen.
//  Das ist eine Zeile mehr und dafür eindeutig: PostgREST liefert eingebettete
//  1:1-Beziehungen je nach erkannter Kardinalität als Objekt oder als Liste,
//  und die Klassenzahl kommt ohnehin aus einer Funktion - ein Plattformkonto
//  darf `classes` nicht lesen, nur zählen lassen.
// ════════════════════════════════════════════════════════════════════════

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { OttoClient } from "@otto/plattform";

export type SchoolInternals = {
  readonly customerNumber: string | null;
  readonly annualPriceCents: number | null;
  readonly notes: string | null;
};

export type School = {
  readonly id: string;
  readonly name: string;
  readonly contactName: string | null;
  readonly contactEmail: string | null;
  readonly autoRenew: boolean;
  readonly licenseUntil: string | null;
  readonly internals: SchoolInternals | null;
  readonly teacherCount: number;
  readonly schoolAdminCount: number;
  readonly classCount: number;
};

export type License = {
  readonly id: string;
  readonly schoolId: string;
  readonly validFrom: string;
  readonly validUntil: string;
  readonly priceCents: number | null;
  readonly source: "manual" | "auto";
  readonly invoicedAt: string | null;
  readonly note: string | null;
};

const SCHOOLS_KEY = ["schools"] as const;
const LICENSES_KEY = ["schoolLicenses"] as const;

/** Die Konten einer Schule. Je Schule ein eigener Schlüssel. */
const peopleKey = (schoolId: string | null) => ["schoolPeople", schoolId] as const;

export const schoolKeys = {
  schools: SCHOOLS_KEY,
  licenses: LICENSES_KEY,
  people: peopleKey,
};

/** Ein Konto an einer Schule. Keine E-Mail – die steht in auth.users. */
export type SchoolPerson = {
  readonly id: string;
  readonly name: string;
  readonly role: "school_admin" | "teacher";
  readonly since: string;
};

/**
 * Wer an dieser Schule ein Konto hat.
 *
 * Die Schulliste zählt nur („2 Lehrkräfte, 1 in der Leitung“). Beim Einladen
 * eines Schuladmins will man aber wissen, WER schon einer ist – sonst lädt man
 * zum zweiten Mal dieselbe Person ein oder sucht in der Schule nach jemandem,
 * den es längst gibt.
 *
 * Erlaubt ist das über `profile_platform_read`. Ausdrücklich nicht dabei und
 * niemals: Kinder. `students` und `progress` haben für die Plattform keine
 * Lesepolicy, und das bleibt so.
 */
export function useSchoolPeople(client: OttoClient, schoolId: string | null) {
  return useQuery({
    queryKey: peopleKey(schoolId),
    enabled: schoolId !== null,
    queryFn: async (): Promise<SchoolPerson[]> => {
      const { data, error } = await client
        .from("profiles")
        .select("id, display_name, role, created_at")
        .eq("school_id", schoolId!)
        .order("display_name");
      if (error) throw error;

      // Die Leitung zuerst: das ist die Frage, die diese Liste beantwortet.
      return (data ?? [])
        .map((row) => ({
          id: row.id,
          name: row.display_name,
          role: row.role as SchoolPerson["role"],
          since: row.created_at,
        }))
        .sort((a, b) =>
          a.role === b.role
            ? a.name.localeCompare(b.name, "de")
            : a.role === "school_admin"
              ? -1
              : 1,
        );
    },
  });
}

export function useSchools(client: OttoClient) {
  return useQuery({
    queryKey: SCHOOLS_KEY,
    queryFn: async (): Promise<School[]> => {
      const [schools, internals, profiles, classCounts] = await Promise.all([
        client
          .from("schools")
          .select("id, name, contact_name, contact_email, auto_renew, license_until")
          .order("name"),
        client
          .from("school_internals")
          .select("school_id, customer_number, annual_price_cents, notes"),
        client.from("profiles").select("school_id, role"),
        client.rpc("school_class_counts"),
      ]);

      for (const part of [schools, internals, profiles, classCounts]) {
        if (part.error) throw part.error;
      }

      const internalsBySchool = new Map(
        (internals.data ?? []).map((row) => [
          row.school_id,
          {
            customerNumber: row.customer_number,
            annualPriceCents: row.annual_price_cents,
            notes: row.notes,
          },
        ]),
      );
      const classesBySchool = new Map(
        (classCounts.data ?? []).map((row) => [row.school_id, Number(row.class_count)]),
      );

      const countOf = (schoolId: string, role: string) =>
        (profiles.data ?? []).filter((p) => p.school_id === schoolId && p.role === role)
          .length;

      return (schools.data ?? []).map((row) => ({
        id: row.id,
        name: row.name,
        contactName: row.contact_name,
        contactEmail: row.contact_email,
        autoRenew: row.auto_renew,
        licenseUntil: row.license_until,
        internals: internalsBySchool.get(row.id) ?? null,
        teacherCount: countOf(row.id, "teacher"),
        schoolAdminCount: countOf(row.id, "school_admin"),
        classCount: classesBySchool.get(row.id) ?? 0,
      }));
    },
  });
}

/** Zeiträume einer Schule, neueste zuerst. */
export function useLicenses(client: OttoClient, schoolId: string | null) {
  return useQuery({
    queryKey: [...LICENSES_KEY, schoolId],
    enabled: schoolId !== null,
    queryFn: async (): Promise<License[]> => {
      const { data, error } = await client
        .from("school_licenses")
        .select(
          "id, school_id, valid_from, valid_until, price_cents, source, invoiced_at, note",
        )
        .eq("school_id", schoolId!)
        .order("valid_from", { ascending: false });
      if (error) throw error;
      return (data ?? []).map(toLicense);
    },
  });
}

/**
 * Alle offenen Zeiträume über alle Schulen.
 *
 * Automatisch verlängerte landen hier von selbst: der Job setzt `invoiced_at`
 * nicht. Genau dafür ist die Liste da - `auto_renew` ist eine Zusage, kein
 * Zahlungsnachweis.
 */
export function useUninvoiced(client: OttoClient) {
  return useQuery({
    queryKey: [...LICENSES_KEY, "uninvoiced"],
    queryFn: async (): Promise<(License & { schoolName: string })[]> => {
      const [licenses, schools] = await Promise.all([
        client
          .from("school_licenses")
          .select(
            "id, school_id, valid_from, valid_until, price_cents, source, invoiced_at, note",
          )
          .is("invoiced_at", null)
          .order("valid_from", { ascending: false }),
        client.from("schools").select("id, name"),
      ]);
      if (licenses.error) throw licenses.error;
      if (schools.error) throw schools.error;

      const nameOf = new Map((schools.data ?? []).map((s) => [s.id, s.name]));
      return (licenses.data ?? []).map((row) => ({
        ...toLicense(row),
        schoolName: nameOf.get(row.school_id) ?? "unbekannte Schule",
      }));
    },
  });
}

type LicenseRow = {
  id: string;
  school_id: string;
  valid_from: string;
  valid_until: string;
  price_cents: number | null;
  source: string;
  invoiced_at: string | null;
  note: string | null;
};

const toLicense = (row: LicenseRow): License => ({
  id: row.id,
  schoolId: row.school_id,
  validFrom: row.valid_from,
  validUntil: row.valid_until,
  priceCents: row.price_cents,
  // Die Datenbank lässt nur 'manual' und 'auto' zu (check constraint); der
  // erzeugte Typ weiß das nicht und sagt text.
  source: row.source === "auto" ? "auto" : "manual",
  invoicedAt: row.invoiced_at,
  note: row.note,
});

/** Zum Anlegen genügt der Name. Alles andere wächst danach. */
export function useCreateSchool(client: OttoClient) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (name: string) => {
      const { data, error } = await client
        .from("schools")
        .insert({ name })
        .select("id")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: SCHOOLS_KEY }),
  });
}

export function useUpdateSchool(client: OttoClient) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      name: string;
      contactName: string | null;
      contactEmail: string | null;
      autoRenew: boolean;
    }) => {
      // `license_until` steht hier bewusst NICHT: es kommt aus dem Trigger auf
      // school_licenses, und das Spaltenrecht fehlt ohnehin.
      const { error } = await client
        .from("schools")
        .update({
          name: input.name,
          contact_name: input.contactName,
          contact_email: input.contactEmail,
          auto_renew: input.autoRenew,
        })
        .eq("id", input.id);
      if (error) throw error;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: SCHOOLS_KEY }),
  });
}

/**
 * Kaufmännisches. `upsert`, weil eine gerade angelegte Schule noch keine Zeile
 * in `school_internals` hat - der Regelfall beim ersten Bearbeiten.
 */
export function useSaveInternals(client: OttoClient) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      schoolId: string;
      customerNumber: string | null;
      annualPriceCents: number | null;
      notes: string | null;
    }) => {
      const { error } = await client.from("school_internals").upsert(
        {
          school_id: input.schoolId,
          customer_number: input.customerNumber,
          annual_price_cents: input.annualPriceCents,
          notes: input.notes,
        },
        { onConflict: "school_id" },
      );
      if (error) throw error;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: SCHOOLS_KEY }),
  });
}

export function useSellLicense(client: OttoClient) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      schoolId: string;
      validFrom: string;
      validUntil: string;
      priceCents: number | null;
      note: string | null;
      createdBy: string | null;
    }) => {
      const { error } = await client.from("school_licenses").insert({
        school_id: input.schoolId,
        valid_from: input.validFrom,
        valid_until: input.validUntil,
        price_cents: input.priceCents,
        source: "manual",
        note: input.note,
        created_by: input.createdBy,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      // Beides: die Historie und die Schulliste. `license_until` in der Liste
      // ändert der Trigger, nicht dieser Aufruf - ohne die zweite Entwertung
      // zeigt die Tabelle den alten Stand und sieht wie ein Fehler aus.
      void queryClient.invalidateQueries({ queryKey: LICENSES_KEY });
      void queryClient.invalidateQueries({ queryKey: SCHOOLS_KEY });
    },
  });
}

/**
 * Abrechnen ist ein Häkchen, kein Buchungssatz. Die Plattform stellt keine
 * Rechnung, sie merkt sich nur, dass es passiert ist.
 */
export function useMarkInvoiced(client: OttoClient) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; invoicedAt: string | null }) => {
      const { error } = await client
        .from("school_licenses")
        .update({ invoiced_at: input.invoicedAt })
        .eq("id", input.id);
      if (error) throw error;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: LICENSES_KEY }),
  });
}
